import React, { FC, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import { selectPlaylistById } from '@store/slices/playlist.slice'
import {
  formatDate,
  toDateHourString,
  toHourMinString,
} from '@shared/tools/time.tools'
import { Col, Row } from 'react-bootstrap'
import {
  AISlotModel,
  AiSlotState,
  selectAiSlotsByIds,
} from '@store/slices/aiSlot.slice'
import {
  fetchAllVoices,
  selectAllVoices,
  selectVoiceLoading,
} from '@store/slices/voice.slice'
import {
  selectAllAuthors,
  selectAuthorByEmail,
  selectAuthorsLoading,
} from '@store/selectors/author.selectors'
import { fetchAllAuthors } from '@store/thunk/author.thunk'
import { AuthorModel } from '@store/slices/author.slice'

const PlaylistDetailComponent: FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { playlistId } = useParams()
  const playlist = useSelector((state: RootState) =>
    playlistId ? selectPlaylistById(state, playlistId) : null
  )
  const aiSlots: AISlotModel[] = useSelector((state: RootState) =>
    playlist?.id ? selectAiSlotsByIds(state, playlist.aiSlots) : null
  )
  const voices = useSelector(selectAllVoices)
  const voiceLoading = useSelector(selectVoiceLoading)
  const defaultVoice = voices.find(
    (voice) => voice.id == playlist?.defaultVoice
  )
  const authors = useSelector(selectAllAuthors)
  const authorsLoadin = useSelector(selectAuthorsLoading)
  const createdBy: AuthorModel | undefined = useSelector((state: RootState) =>
    playlist ? selectAuthorByEmail(state, playlist.createdBy) : undefined
  )
  const updatedBy: AuthorModel | undefined = useSelector((state: RootState) =>
    playlist ? selectAuthorByEmail(state, playlist.updatedBy) : undefined
  )

  useEffect(() => {
    if ((!authors || authors.length === 0) && !authorsLoadin) {
      dispatch(fetchAllAuthors())
    }
  }, [authors])

  useEffect(() => {
    if (voices?.length == 0 && !voiceLoading) {
      dispatch(fetchAllVoices())
    }
  }, [voices])

  const showProgress = () => {
    const audio = aiSlots?.reduce((accumulator, item) => {
      if (
        item.state <= AiSlotState.AUDIO &&
        item.state > AiSlotState.PUBLISHED
      ) {
        return accumulator + 1
      } else {
        return accumulator
      }
    }, 0)
    const outline = aiSlots?.reduce((accumulator, item) => {
      if (item.state == AiSlotState.OUTLINE) {
        return accumulator + 1
      } else {
        return accumulator
      }
    }, 0)
    const text = aiSlots?.reduce((accumulator, item) => {
      if (item.state <= AiSlotState.TEXT && item.state > AiSlotState.AUDIO) {
        return accumulator + 1
      } else {
        return accumulator
      }
    }, 0)
    const newState = aiSlots?.reduce((accumulator, item) => {
      if (item.state == AiSlotState.NEW) {
        return accumulator + 1
      } else {
        return accumulator
      }
    }, 0)
    const published = aiSlots?.reduce((accumulator, item) => {
      if (item.state == AiSlotState.PUBLISHED) {
        return accumulator + 1
      } else {
        return accumulator
      }
    }, 0)
    const total = aiSlots?.length
    return (
      <div className={'d-flex gap-2'}>
        <span>
          Sloty publikováno: {published}/<strong>{total}</strong>
        </span>
        <span>audio: {audio}</span>
        <span>text: {text}</span>
        <span>outline: {outline}</span>
        <span>nové: {newState}</span>
      </div>
    )
  }

  return (
    <div className={'d-flex'}>
      <Col xs={8}>
        <div className={'d-flex gap-2'}>
          <span>
            Playlist&nbsp;
            <strong>
              {playlist && formatDate(playlist.fromTime)}{' '}
              {playlist && toHourMinString(playlist.fromTime)}-
              {playlist && toHourMinString(playlist.toTime)}&nbsp;
              {Intl.DateTimeFormat().resolvedOptions().timeZone}
            </strong>
          </span>
          <span className="vr"></span>
          <span>
            Stanice&nbsp;<strong>{playlist?.stationId}</strong>
          </span>
          <span className="vr"></span>
          <span>
            Přednastavený hlas&nbsp;
            <strong>{defaultVoice?.name}</strong>
          </span>
        </div>
        <div>{showProgress()}</div>
      </Col>
      <Col className={'d-flex flex-column text-secondary'}>
        <Row className={'justify-content-end'}>
          {'vytvořeno: '}
          {`${playlist && toDateHourString(playlist.createdAt)} ${!!createdBy?.username ? createdBy.username : createdBy?.email}`}
        </Row>
        <Row className={'justify-content-end'}>
          {'naposledy upraveno: '}
          {`${playlist && toDateHourString(playlist.updatedAt)} ${!!updatedBy?.username ? updatedBy.username : updatedBy?.email}`}
        </Row>
      </Col>
    </div>
  )
}

export default PlaylistDetailComponent
